import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';

interface ScrollableContainerProps {
  children: React.ReactNode;
  className?: string;
  maxHeight?: string;
  style?: React.CSSProperties;
}

const StyledScrollableContainer = styled.div<{ maxHeight?: string; showScrollbar?: boolean }>`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  ${({ maxHeight }) => maxHeight && `max-height: ${maxHeight};`}
  
  /* Professional thin scrollbar overlay - auto-hides */
  scrollbar-width: thin;
  scrollbar-color: ${({ showScrollbar }) => 
    showScrollbar 
      ? `${theme.colors.primary[400]} transparent` 
      : 'transparent transparent'};
  
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  /* Hide scrollbar buttons (arrows) */
  &::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, ${theme.colors.primary[400]} 0%, ${theme.colors.primary[500]} 100%);
    border-radius: 4px;
    opacity: ${({ showScrollbar }) => showScrollbar ? 0.6 : 0};
    transition: opacity 0.8s ease 1s;
  }
  
  &:hover::-webkit-scrollbar-thumb {
    opacity: 0.8;
    transition: opacity 0.2s ease 0s;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    opacity: 1;
    background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  }
  
  /* Hide scrollbar corner */
  &::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

export const ScrollableContainer: React.FC<ScrollableContainerProps> = ({ 
  children, 
  className,
  maxHeight,
  style
}) => {
  const [showScrollbar, setShowScrollbar] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    // Show scrollbar immediately when scrolling
    setShowScrollbar(true);
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Hide scrollbar after 1.5 seconds of no scrolling
    scrollTimeoutRef.current = setTimeout(() => {
      setShowScrollbar(false);
    }, 1500);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('mouseenter', () => setShowScrollbar(true));
      container.addEventListener('mouseleave', () => {
        // Only hide if not scrolling
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
          setShowScrollbar(false);
        }, 1500);
      });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }
  }, []);

  return (
    <StyledScrollableContainer 
      ref={containerRef}
      className={className} 
      maxHeight={maxHeight} 
      style={style}
      showScrollbar={showScrollbar}
    >
      {children}
    </StyledScrollableContainer>
  );
};

export default ScrollableContainer;

